import { Button } from '@headlessui/react'
import { Printer, RefreshCw } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import PickPrint from './PickPrint'
import { useOutboundActions, useOutboundState } from './stores/outboundStore'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import { HouseBillScheduledSchema } from '@/services/OutboundV2Service'
import { useUIStore } from '@/stores/useUIStore'

const COLUMNS = [
  {
    key: 'PENDING_OUTBOUND',
    label: '待出库',
    headerStyle: 'bg-blue-50 border-blue-200',
    badgeStyle: 'bg-blue-100 text-blue-700',
    cardStyle:
      'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg hover:shadow-blue-200/50'
  },
  {
    key: 'OUTBOUND_IN_PROGRESS',
    label: '出库中',
    headerStyle: 'bg-amber-50 border-amber-200',
    badgeStyle: 'bg-amber-100 text-amber-700',
    cardStyle:
      'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 hover:shadow-lg hover:shadow-amber-200/50'
  },
  {
    key: 'PENDING_DELIVERY',
    label: '待配送',
    headerStyle: 'bg-emerald-50 border-emerald-200',
    badgeStyle: 'bg-emerald-100 text-emerald-700',
    cardStyle:
      'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 hover:shadow-lg hover:shadow-emerald-200/50'
  },
  {
    key: 'PENDING_CONFIRMATION',
    label: '待确认',
    headerStyle: 'bg-purple-50 border-purple-200',
    badgeStyle: 'bg-purple-100 text-purple-700',
    cardStyle:
      'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg hover:shadow-purple-200/50'
  }
] as const

type ColumnKey = (typeof COLUMNS)[number]['key']

const OutboundIndex: React.FC = () => {
  const { showError, showSuccess } = useUIStore()
  const { loading, houseBillsWithWarehouse, printingHouseBill } =
    useOutboundState()
  const {
    openLoading,
    closeLoading,
    openPrintDialog,
    fetchHouseBillsWithWarehouse,
    setPrintingHouseBill
  } = useOutboundActions()

  const [dateFilter, setDateFilter] = useState<string>(() => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })

  useEffect(() => {
    fetchOutboundHouseBills()
  }, [])

  const fetchOutboundHouseBills = async () => {
    openLoading()
    try {
      await fetchHouseBillsWithWarehouse()

      houseBillsWithWarehouse.sort((a, b) => {
        const dateA = new Date(a.scheduledOutboundDate || '')
        const dateB = new Date(b.scheduledOutboundDate || '')
        return dateA.getTime() - dateB.getTime()
      })
    } catch (error) {
      console.error('获取出库 HBL 数据失败:', error)
      showError('获取出库 HBL 数据失败')
    } finally {
      closeLoading()
    }
  }

  const printOutboundOrder = async (houseBillId: number) => {
    try {
      const houseBill = houseBillsWithWarehouse.find(
        (hb) => hb.id === houseBillId
      )
      if (!houseBill) {
        throw new Error('未找到对应的出库单')
      }
      setPrintingHouseBill(houseBill)
      openPrintDialog()
      showSuccess('捡货单生成成功')
    } catch (error) {
      console.error('捡货单生成失败:', error)
      showError('捡货单生成失败')
    }
  }

  const getFilteredHouseBills = () => {
    if (!dateFilter) return houseBillsWithWarehouse
    return houseBillsWithWarehouse.filter(
      (hb) => hb.scheduledOutboundDate === dateFilter
    )
  }

  const getHouseBillBoxCount = (houseBill: HouseBillScheduledSchema) => {
    return (
      houseBill.warehousePallets?.reduce((total, pallet) => {
        return (
          total +
          (pallet.cargoMarkAssociations?.reduce((markTotal, mark) => {
            return markTotal + (mark.quantity || 0)
          }, 0) || 0)
        )
      }, 0) || 0
    )
  }

  const filteredHouseBills = getFilteredHouseBills()

  const todayCount = houseBillsWithWarehouse.filter(
    (hb) => hb.scheduledOutboundDate === new Date().toISOString().split('T')[0]
  ).length

  const totalPallets = houseBillsWithWarehouse.reduce(
    (total, hb) => total + (hb.warehousePallets?.length || 0),
    0
  )

  const totalBoxes = houseBillsWithWarehouse.reduce((total, hb) => {
    return (
      total +
      (hb.warehousePallets?.reduce((palletTotal, pallet) => {
        return (
          palletTotal +
          (pallet.cargoMarkAssociations?.reduce(
            (markTotal, mark) => markTotal + (mark.quantity || 0),
            0
          ) || 0)
        )
      }, 0) || 0)
    )
  }, 0)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    )
  }

  const getColumnBills = (columnKey: ColumnKey) =>
    filteredHouseBills
      .filter((hb) => hb.status === columnKey)
      .sort((a, b) => {
        const dateA = new Date(a.scheduledOutboundDate || '').getTime()
        const dateB = new Date(b.scheduledOutboundDate || '').getTime()
        return dateA - dateB
      })

  return (
    <div className="p-4 h-full flex flex-col">
      <PickPrint />

      {/* 头部 */}
      <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <div className="flex items-center space-x-4">
          <h1 className="text-lg font-semibold">配送任务</h1>
          <div className="flex items-center space-x-3 text-sm">
            <span className="text-gray-600">
              总数: {houseBillsWithWarehouse.length}
            </span>
            <span className="text-gray-600">今日: {todayCount}</span>
            <span className="text-gray-600">托盘: {totalPallets}</span>
            <span className="text-gray-600">箱货: {totalBoxes}</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-xs"
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter('')}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              清除
            </button>
          )}
          <button
            onClick={fetchOutboundHouseBills}
            disabled={loading}
            className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className="w-3 h-3" />
            <span>刷新</span>
          </button>
        </div>
      </div>

      {/* 看板列 */}
      <div className="flex-1 grid grid-cols-4 gap-3 min-h-0">
        {COLUMNS.map((col) => {
          const bills = getColumnBills(col.key)
          return (
            <div key={col.key} className="flex flex-col min-h-0">
              {/* 列标题 */}
              <div
                className={`flex items-center justify-between px-3 py-2 rounded-t border ${col.headerStyle} flex-shrink-0`}
              >
                <span className="text-sm font-medium">{col.label}</span>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${col.badgeStyle}`}
                >
                  {bills.length}
                </span>
              </div>

              {/* 卡片列表（独立滚动） */}
              <div className="flex-1 overflow-y-auto space-y-2 p-2 bg-gray-50 rounded-b border border-t-0 border-gray-200">
                {bills.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-xs">
                    暂无数据
                  </div>
                ) : (
                  bills.map((houseBill) => (
                    <div
                      key={houseBill.houseBlNumber}
                      className={`${col.cardStyle} border rounded p-3 transition-all duration-200 flex flex-col`}
                    >
                      {/* 头部 */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-sm truncate flex-1 min-w-0">
                          {houseBill.houseBlNumber}
                        </div>
                        <Button
                          onClick={() => printOutboundOrder(houseBill.id)}
                          disabled={printingHouseBill?.id === houseBill.id}
                          className="flex items-center space-x-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 ml-2 flex-shrink-0"
                        >
                          {printingHouseBill?.id === houseBill.id ? (
                            <span>生成中...</span>
                          ) : (
                            <>
                              <Printer className="w-3 h-3" />
                              <span>捡货单</span>
                            </>
                          )}
                        </Button>
                      </div>

                      {/* 基本信息 */}
                      <div className="text-xs text-gray-500 space-y-1 mb-2">
                        <div>预约: {houseBill.scheduledOutboundDate}</div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                          <span>
                            唛头:{' '}
                            <strong className="text-gray-700">
                              {houseBill.marksNumbers?.length || 0}
                            </strong>
                          </span>
                          <span>
                            重量:{' '}
                            <strong className="text-gray-700">
                              {houseBill.weightKg || '-'}
                            </strong>{' '}
                            KG
                          </span>
                          <span>
                            托盘:{' '}
                            <strong className="text-blue-600">
                              {houseBill.warehousePallets?.length || 0}
                            </strong>
                          </span>
                          <span>
                            箱数:{' '}
                            <strong className="text-gray-700">
                              {getHouseBillBoxCount(houseBill)}
                            </strong>
                          </span>
                        </div>
                      </div>

                      {/* 唛头标签 */}
                      {houseBill.cargoMarks &&
                        houseBill.cargoMarks.length > 0 && (
                          <div className="pt-2 border-t border-gray-100">
                            <div className="flex flex-wrap gap-1">
                              {houseBill.cargoMarks.slice(0, 3).map((cargo) => (
                                <span
                                  key={cargo.id}
                                  className="bg-white/70 px-1.5 py-0.5 rounded text-xs"
                                >
                                  {cargo.cargoNumber} ({cargo.packages})
                                </span>
                              ))}
                              {houseBill.cargoMarks.length > 3 && (
                                <span className="text-xs text-gray-400">
                                  +{houseBill.cargoMarks.length - 3}个
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                      {/* 备注 */}
                      {houseBill.note && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-600 line-clamp-2">
                            备注: {houseBill.note}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default OutboundIndex
